import React from "react";
import { IconType } from "react-icons";

import { TArg, tw } from "utils/tw";

export interface Props
  extends Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    "className"
  > {
  id: string;
  label: string;
  type?: string;
  hint?: string;
  helpText?: string;
  errorMessage?: string;
  LeadingIcon?: IconType;
  leadingAction?: () => void;
  TrailingIcon?: IconType;
  trailingAction?: () => void;
  hideLabel?: boolean;
  fullWidth?: boolean;
  className?: TArg;
}

export default React.forwardRef(
  (
    {
      type = "text",
      id,
      label,
      hint,
      helpText,
      errorMessage,
      LeadingIcon,
      leadingAction,
      TrailingIcon,
      trailingAction,
      hideLabel = false,
      fullWidth = false,
      className,
      ...props
    }: Props,
    ref: React.ForwardedRef<HTMLInputElement>
  ): React.JSX.Element => {
    const hasHelpText = helpText !== undefined;
    const hasError = errorMessage !== undefined;

    const helpTextId = hasHelpText ? `${id}-help` : "";
    const errorId = hasError ? `${id}-error` : "";
    const inputDescriptionIDs =
      hasHelpText || hasError ? `${helpTextId} ${errorId}`.trim() : undefined;

    const containerStyles = tw(className, { "w-full": fullWidth });

    const labelContainerStyles = tw("flex", "justify-between");

    const labelStyles = tw("block", "text-sm", "text-gray-700", {
      "sr-only": hideLabel,
    });

    const hintStyles = tw("text-sm", "text-gray-500");

    const inputContainerStyles = tw(
      "mt-1",
      "relative",
      "rounded-md",
      "shadow-sm"
    );

    const inputBase = tw(
      "block",
      "w-full",
      "shadow-sm",
      "sm:text-sm",
      "rounded-md",
      "focus:ring-gray-900",
      "focus:border-gray-900"
    );
    const inputError = tw("border-2", "border-error", "bg-error-light");
    const inputDisabled = tw("bg-gray-100", "text-gray-700");
    const inputStyles = tw(inputBase, {
      "border-gray-300": !hasError,
      [inputError as string]: hasError,
      [inputDisabled as string]: props.disabled || props.readOnly,
      "pr-10": TrailingIcon !== undefined,
      "pl-10": LeadingIcon !== undefined,
    });

    const iconWrapperBase = tw(
      "px-3",
      "absolute",
      "inset-y-0",
      "flex",
      "items-center"
    );
    const hasLeadingAction = typeof leadingAction !== "undefined";
    const leadingIconWrapperStyles = tw(
      iconWrapperBase,
      hasLeadingAction ? "cursor-pointer" : "pointer-events-none",
      "left-0"
    );
    const hasTrailingAction = typeof trailingAction !== "undefined";
    const trailingIconWrapperStyles = tw(
      iconWrapperBase,
      hasTrailingAction ? "cursor-pointer" : "pointer-events-none",
      "right-0"
    );

    const iconStyles = tw("h-5", "w-5", {
      "text-gray-400": !hasError,
      "text-error": hasError,
    });

    const textBase = tw("mt-2", "text-sm");
    const helpTextStyles = tw(textBase, "text-gray-700");
    const errorMessageStyles = tw(textBase, "text-error");

    return (
      <div className={containerStyles}>
        <div className={labelContainerStyles}>
          <label htmlFor={id} className={labelStyles}>
            {label}
          </label>

          {hint && <span className={hintStyles}>{hint}</span>}
        </div>

        <div className={inputContainerStyles}>
          {LeadingIcon &&
            (leadingAction ? (
              <button
                type="button"
                className={leadingIconWrapperStyles}
                onClick={leadingAction}
              >
                <LeadingIcon className={iconStyles} />
              </button>
            ) : (
              <div className={leadingIconWrapperStyles}>
                <LeadingIcon className={iconStyles} />
              </div>
            ))}

          <input
            {...props}
            id={id}
            ref={ref}
            type={type}
            className={inputStyles}
            aria-invalid={hasError}
            aria-describedby={inputDescriptionIDs}
          />

          {TrailingIcon &&
            (trailingAction ? (
              <button
                type="button"
                className={trailingIconWrapperStyles}
                onClick={trailingAction}
              >
                <TrailingIcon className={iconStyles} />
              </button>
            ) : (
              <div className={trailingIconWrapperStyles}>
                <TrailingIcon className={iconStyles} />
              </div>
            ))}
        </div>

        {helpTextId && (
          <p className={helpTextStyles} id={helpTextId}>
            {helpText}
          </p>
        )}

        {errorId && (
          <p className={errorMessageStyles} id={errorId}>
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);
